"use client";

import { createContext, useContext, useEffect, useReducer, ReactNode, useState } from "react";

export interface CartItem {
  id: string;
  name: string;
  image: string;
  subcategory?: string;
  category: string;
  categorySlug: string;
  model?: string;
  qty: number;
}

type Action =
  | { type: "ADD"; item: Omit<CartItem, "qty"> }
  | { type: "REMOVE"; id: string }
  | { type: "UPDATE_QTY"; id: string; qty: number }
  | { type: "CLEAR" }
  | { type: "HYDRATE"; items: CartItem[] };

function reducer(state: CartItem[], action: Action): CartItem[] {
  switch (action.type) {
    case "ADD": {
      const existing = state.find((i) => i.id === action.item.id);
      if (existing) {
        return state.map((i) => (i.id === action.item.id ? { ...i, qty: i.qty + 1 } : i));
      }
      return [...state, { ...action.item, qty: 1 }];
    }
    case "REMOVE":
      return state.filter((i) => i.id !== action.id);
    case "UPDATE_QTY":
      return state.map((i) => (i.id === action.id ? { ...i, qty: Math.max(1, action.qty) } : i));
    case "CLEAR":
      return [];
    case "HYDRATE":
      return action.items;
    default:
      return state;
  }
}

interface CartContextValue {
  items: CartItem[];
  count: number;
  addToCart: (item: Omit<CartItem, "qty">) => void;
  removeFromCart: (id: string) => void;
  updateQty: (id: string, qty: number) => void;
  clearCart: () => void;
  miniOpen: boolean;
  setMiniOpen: (v: boolean) => void;
}

const CartContext = createContext<CartContextValue | null>(null);

const STORAGE_KEY = "kasai-cart-v1";

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, dispatch] = useReducer(reducer, []);
  const [miniOpen, setMiniOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) dispatch({ type: "HYDRATE", items: JSON.parse(raw) });
    } catch {}
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items, hydrated]);

  const value: CartContextValue = {
    items,
    count: items.reduce((s, i) => s + i.qty, 0),
    addToCart: (item) => {
      dispatch({ type: "ADD", item });
      setMiniOpen(true);
    },
    removeFromCart: (id) => dispatch({ type: "REMOVE", id }),
    updateQty: (id, qty) => dispatch({ type: "UPDATE_QTY", id, qty }),
    clearCart: () => dispatch({ type: "CLEAR" }),
    miniOpen,
    setMiniOpen,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
